import React, { useRef, useState } from "react";
import { api, resolveImage } from "../lib/api";
import { toast } from "sonner";
import { Upload, X, Loader2 } from "lucide-react";

export default function ImageUploader({
  value,
  onChange,
  label = "Image",
  testId = "image-uploader",
}) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div data-testid={testId} className="space-y-3">
      <label className="text-[10px] tracking-[0.25em] uppercase text-[#73706B] block">
        {label}
      </label>
      {value ? (
        <div className="relative inline-block">
          <img
            src={resolveImage(value)}
            alt="preview"
            className="w-full max-w-sm aspect-video object-cover border border-[#E5DFD5]"
          />
          <button
            type="button"
            onClick={() => onChange("")}
            data-testid={`${testId}-clear`}
            className="absolute top-2 right-2 bg-[#1A1A1A] text-[#F9F6F0] p-1 hover:bg-[#C5A880]"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      ) : (
        <div className="border border-dashed border-[#E5DFD5] bg-[#FFFFFF] aspect-video max-w-sm flex items-center justify-center text-[#73706B] text-sm">
          No image
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={handleFile}
          className="hidden"
          data-testid={`${testId}-file`}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`${testId}-upload-btn`}
          className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#F9F6F0] px-4 py-2 text-[10px] tracking-[0.25em] uppercase hover:bg-[#C5A880] transition-colors disabled:opacity-60"
        >
          {uploading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
          ) : (
            <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />
          )}
          {uploading ? "Uploading" : "Upload"}
        </button>
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="…or paste image URL"
          data-testid={`${testId}-url`}
          className="flex-1 min-w-[200px] bg-transparent border-b border-[#E5DFD5] py-2 text-sm focus:outline-none focus:border-[#C5A880]"
        />
      </div>
      <p className="text-[10px] tracking-[0.18em] uppercase text-[#9A968D] flex items-center gap-1.5" data-testid={`${testId}-deploy-safe`}>
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#7BA890]" /> Media auto-saves in database — deploy-safe
      </p>
    </div>
  );
}
